import React, { useState } from 'react'

import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Fade from '@material-ui/core/Fade'
import LinearProgress from '@material-ui/core/LinearProgress'
import { makeStyles } from '@material-ui/core/styles'
import { DropzoneArea } from 'material-ui-dropzone'
import { useTranslate } from 'react-admin'

import {
  lightergray2,
  lightergray3,
  lightGray,
  lightOrange,
  orange,
  white,
} from '../../../constants/colorConstants'
import tracker from '../../../utils/tracker'
import acceptedFiles from './accepterFiles'

const useStyles = makeStyles((theme) => ({
  button: {
    backgroundColor: orange,
    border: `solid 1px ${orange}!important`,
    padding: '10px 20px',
    borderRadius: '28px',
    ...theme.mundi.font.roboto400Normal,
    color: white,
    fontSize: '13px',
    lineHeight: '15.23px',
    marginBottom: '20px',
    marginRight: '5px',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: orange,
    },
  },
  buttonDisabled: {
    backgroundColor: lightergray2,
    border: `solid 1px ${lightergray2}!important`,
    padding: '10px 20px',
    borderRadius: '28px',
    ...theme.mundi.font.roboto400Normal,
    color: lightGray,
    fontSize: '13px',
    lineHeight: '15.23px',
    marginBottom: '30px',
    marginRight: '5px',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: lightergray3,
    },
  },
  secondaryButton: {
    backgroundColor: white,
    border: `solid 1px ${orange}!important`,
    padding: '10px 20px',
    borderRadius: '28px',
    ...theme.mundi.font.roboto400Normal,
    color: orange,
    fontSize: '13px',
    lineHeight: '15.23px',
    marginBottom: '20px',
    marginRight: '5px',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: lightOrange,
    },
  },
  linearProgress: {
    marginTop: 15,
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
  },
  formControl: {
    zIndex: 1,
    marginTop: 20,
    minWidth: 220,
    maxWidth: 220,
  },
  previewChip: {
    minWidth: 160,
    maxWidth: 210,
  },
  uploadedFile: {
    borderWidth: 0.5,
    borderColor: theme.palette.secondary.main,
    borderStyle: 'solid',
    borderRadius: 5,
    padding: 10,
  },
  uploadedFileTitle: {
    ...theme.mundi.font.roboto500Normal,
    color: theme.palette.secondary.main,
    maxWidth: 160,
    wordWrap: 'break-word',
    marginLeft: 10,
  },
  uploadedFileIcon: {
    color: theme.palette.secondary.main,
  },
  root: {
    color: theme.palette.secondary.main,
  },
  uploadFilesPanel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  [theme.breakpoints.down('sm')]: {
    formControl: {
      marginTop: 20,
      minWidth: '25em',
      maxWidth: '25em',
    },
  },
  [theme.breakpoints.down('xs')]: {
    formControl: {
      marginTop: 20,
      minWidth: '20em',
      maxWidth: '20em',
    },
  },
}))

export const UploadDialog = ({
  state,
  setState,
  buildFileUploadState,
  savedFiles,
  setSaveFiles,
  uploadFiles,
  changesCount,
  setChangedCount,
  invoiceId,
  enqueueSnackbar,
  closeSnackbar,
  setSnackbarKeys,
  filesLimit,
  timestamp,
}) => {
  const classes = useStyles()
  const translate = useTranslate()
  const [currentFilesCount, setCurrentFilesCount] = useState(0)

  const handleClose = () => {
    tracker.event({
      action: 'select_file_batches_before_cancel_clicked',
      category: 'upload',
      label: 'Count',
      value: changesCount - 1,
    })
    setChangedCount(0)

    tracker.event({
      action: 'upload_cancel_button_clicked',
      category: 'upload',
      label: 'SelectedFilesCount',
      value: currentFilesCount,
    })
    setCurrentFilesCount(0)

    setState({
      ...buildFileUploadState(state.files, false, true, false),
    })
  }

  const handleChange = (files) => {
    setChangedCount(changesCount + 1)
    setCurrentFilesCount(files.length)

    if (!files?.length && savedFiles.length > 0) {
      tracker.event({
        action: 'upload_files_dialog_file_removed',
        category: 'upload',
      })
    }

    if (files?.length > 0) {
      setState({
        ...buildFileUploadState(files, false, true, state.open),
      })
    }
  }

  const handleDrop = (file, event) => {
    if (event?.nativeEvent?.type !== 'drop') {
      tracker.event({
        action: 'select_file_clicked',
        category: 'upload',
      })
    } else {
      tracker.event({
        action: 'select_file_dragged',
        category: 'upload',
      })
    }

    tracker.event({
      action: 'select_file_dropped',
      category: 'upload',
    })
  }

  const closeSnackbars = (closeSnackbar, keys) => {
    if (typeof keys !== 'undefined' && keys.length > 0) {
      keys.forEach((key) => {
        closeSnackbar(key)
      })
    }
  }

  const handleSave = async () => {
    tracker.event({
      action: 'select_file_batches_before_accept_clicked',
      category: 'upload',
      label: 'Count',
      value: changesCount - 1,
    })
    setChangedCount(0)

    tracker.event({
      action: 'upload_accept_button_clicked',
      category: 'upload',
      label: 'SelectedFilesCount',
      value: currentFilesCount,
    })
    setCurrentFilesCount(0)

    tracker.event({
      action: 'upload_started',
      category: 'upload',
    })

    setState({
      ...buildFileUploadState(state.files, true, false, true),
    })

    let snackbarKeys = state.files.map((file) =>
      enqueueSnackbar(
        `${translate(
          'resources.invoices.actions.create.upload.uploadFileDialog.uploadingFile'
        ).replace('{{fileName}}', file.name)}`,
        {
          persist: state.uploading,
          variant: 'info',
        }
      )
    )
    setSnackbarKeys(snackbarKeys)
    try {
      const response = await uploadFiles(state.files, timestamp, invoiceId)
      tracker.event({
        action: 'upload_ended',
        category: 'upload',
      })

      if (!response.data[0].success) {
        snackbarKeys = state.files.map((file) =>
          enqueueSnackbar(
            `${translate(
              'resources.invoices.actions.create.upload.fileUploadError'
            ).replace('{{fileName}}', file.name)}`,
            { variant: 'error', persist: true }
          )
        )
        setSnackbarKeys(snackbarKeys)
      }
      const uploadedFiles = response.data.map(
        ({ error, success, ...keepAttrs }) => keepAttrs.data
      )
      const newFiles = [...savedFiles, ...uploadedFiles]
      setSaveFiles(newFiles)

      setState({
        ...buildFileUploadState(newFiles, false, true, false),
      })

      closeSnackbars(closeSnackbar, snackbarKeys)
      state.files.map((item) =>
        enqueueSnackbar(
          `${translate(
            'resources.invoices.actions.create.upload.uploadFileDialog.fileUploaded'
          ).replace('{{fileName}}', item.name)}`,
          { autoHideDuration: 3000, variant: 'success' }
        )
      )
    } catch (e) {
      tracker.event({
        action: 'upload_error',
        category: 'upload',
      })
      setState({
        ...buildFileUploadState(state.files, false, true, true),
      })
      closeSnackbars(closeSnackbar, snackbarKeys)
      const action = (key) => (
        <>
          <Button
            onClick={() => {
              closeSnackbar(key)
            }}
          >
            {translate(
              'resources.invoices.actions.create.upload.uploadFileDialog.dismissSnackBar'
            )}
          </Button>
        </>
      )
      state.files.map((item) =>
        enqueueSnackbar(
          `${translate(
            'resources.invoices.actions.create.upload.uploadFileDialog.fileUploadError'
          ).replace('{{fileName}}', item.name)}`,
          { action, variant: 'error', persist: true }
        )
      )
    }
  }

  const getDropRejectMessage = (rejectedFile) => {
    enqueueSnackbar(
      `${translate(
        'resources.invoices.actions.create.upload.uploadFileDialog.maxFileSizeMessage'
      ).replace('{{fileName}}', rejectedFile.name)}`,
      { autoHideDuration: 3000, variant: 'error' }
    )
  }

  const getFileLimitExceedMessage = () => {
    enqueueSnackbar(
      `${translate(
        'resources.invoices.actions.create.upload.uploadFileDialog.maxFilesUploaded'
      ).replace('{{filesLimit}}', filesLimit)}`,
      { autoHideDuration: 3000, variant: 'error' }
    )
  }

  return (
    <Dialog
      open={state.open}
      onClose={handleClose}
      disableBackdropClick={state.uploading}
      disableEscapeKeyDown={state.uploading}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle>
        {translate(
          'resources.invoices.actions.create.upload.uploadFileDialog.title'
        )}
      </DialogTitle>
      <DialogContent>
        <DropzoneArea
          name={'invoiceFiles'}
          fullWidth
          classes={{
            root: classes.root,
            active: classes.root,
            disabled: classes.disabled,
            invalid: classes.root,
            textContainer: classes.root,
            text: classes.root,
            icon: classes.root,
          }}
          acceptedFiles={acceptedFiles}
          open={state.open}
          filesLimit={filesLimit - savedFiles.length}
          maxFileSize={52428800}
          showAlerts={false}
          showPreviews
          onChange={handleChange}
          onDrop={handleDrop}
          showPreviewsInDropzone={false}
          previewText={translate(
            'resources.invoices.actions.create.upload.uploadFileDialog.previewText'
          )}
          dropzoneText={translate(
            'resources.invoices.actions.create.upload.uploadFileDialog.text'
          )}
          getDropRejectMessage={getDropRejectMessage}
          getFileLimitExceedMessage={getFileLimitExceedMessage}
          useChipsForPreview
          previewGridProps={{
            container: {
              spacing: 1,
              direction: 'row',
            },
          }}
          previewChipProps={{
            classes: {
              root: classes.previewChip,
            },
          }}
          styles={{
            dropzone: {
              width: 400,
              height: 200,
            },
            dropzoneActive: {
              borderColor: orange,
            },
          }}
          dropzoneProps={{
            disabled: state.uploading,
            noKeyboard: state.uploading,
            multiple: true,
            noClick: state.uploading,
          }}
        />
        <Fade
          in={state.uploading}
          style={{
            transitionDelay: state.uploading ? '800ms' : '0ms',
          }}
          unmountOnExit
        >
          <div className={classes.linearProgress}>
            <LinearProgress color="secondary" />
          </div>
        </Fade>
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.secondaryButton}
          onClick={handleClose}
          color="primary"
          disabled={state.uploading}
        >
          {translate(
            'resources.invoices.actions.create.upload.uploadFileDialog.cancel'
          )}
        </Button>
        <Button
          className={
            currentFilesCount === 0 || !state.uploadStatus
              ? classes.buttonDisabled
              : classes.button
          }
          variant="contained"
          onClick={handleSave}
          disabled={currentFilesCount === 0 || !state.uploadStatus}
        >
          {state.uploading && <CircularProgress size={14} />}
          {!state.uploading &&
            translate(
              'resources.invoices.actions.create.upload.uploadFileDialog.submit'
            )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
